import React, { useEffect, useState } from 'react';
import Layout from '../../../components/Layout';
import { apiFetch } from '../../../lib/api';
import { useFiltroSocieta } from '../../../hooks/useFiltroSocieta';
import BopRowActions, { RowIcon } from '../../../components/BopRowActions';

// ── Tipi ──────────────────────────────────────────────────────────────────────

type TipoDeposito = 'FISSO' | 'CONTO_TERZI' | 'COMMESSA';

interface Deposito {
  id: number;
  societaId: number;
  societaNome?: string;
  nome: string;
  tipo: TipoDeposito;
  indirizzo: string | null;
  attivo: boolean;
  commessaId: number | null;
  createdAt: string;
  updatedAt: string;
}

interface FormData {
  societaId: number;
  nome: string;
  tipo: TipoDeposito;
  indirizzo: string;
}

function emptyForm(societaId = 0): FormData {
  return { societaId, nome: '', tipo: 'FISSO', indirizzo: '' };
}

// ── Badge e label ─────────────────────────────────────────────────────────────

const TIPO_LABEL: Record<TipoDeposito, string> = {
  FISSO: 'Fisso',
  CONTO_TERZI: 'Conto Terzi',
  COMMESSA: 'Commessa',
};

function TipoBadge({ tipo }: { tipo: TipoDeposito }) {
  return (
    <span className={`bop-badge bop-depositi__badge--${tipo.toLowerCase()}`}>
      {TIPO_LABEL[tipo]}
    </span>
  );
}

// ── Componente principale ─────────────────────────────────────────────────────

interface DepositiPageProps {
  // true: mostra solo depositi di tipo FISSO (filtro tipo bloccato)
  // false/assente: mostra tutti i tipi di deposito
  soloFisso?: boolean;
}

export default function DepositiPage({ soloFisso = false }: DepositiPageProps) {
  const [items, setItems]       = useState<Deposito[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [modal, setModal]       = useState(false);
  const [form, setForm]         = useState<FormData>(emptyForm());
  const [editId, setEditId]     = useState<number | null>(null);
  const [saving, setSaving]     = useState(false);

  // Filtri
  const [filtroTipo, setFiltroTipo]           = useState('');
  const [mostraDisattivi, setMostraDisattivi] = useState(false);

  const {
    societaId: filtroSocietaId,
    setSocietaId: setFiltroSocietaId,
    listaSocieta: societa,
    isUnica,
    pronta,
  } = useFiltroSocieta();

  const load = async () => {
    if (!pronta) return;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (filtroSocietaId) params.set('societaId', filtroSocietaId);
      const tipoEffettivo = soloFisso ? 'FISSO' : filtroTipo;
      if (tipoEffettivo)   params.set('tipo', tipoEffettivo);
      if (mostraDisattivi) params.set('attivo', 'false');

      const depRes = await apiFetch<{ depositi: Deposito[] }>(`/api/depositi?${params}`);
      setItems(depRes.depositi ?? []);
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [pronta, filtroSocietaId, filtroTipo, mostraDisattivi, soloFisso]); // eslint-disable-line

  // ── Modal helpers ────────────────────────────────────────────────────────────

  const openCreate = () => {
    const sid = filtroSocietaId ? Number(filtroSocietaId) : (societa[0]?.id ?? 0);
    setForm(emptyForm(sid));
    setEditId(null); setModal(true); setError('');
  };

  const openEdit = (d: Deposito) => {
    setForm({ societaId: d.societaId, nome: d.nome, tipo: d.tipo, indirizzo: d.indirizzo ?? '' });
    setEditId(d.id); setModal(true); setError('');
  };

  const closeModal = () => { setModal(false); setError(''); };

  // ── Salva ────────────────────────────────────────────────────────────────────

  const save = async () => {
    if (!form.nome.trim()) { setError('Il nome è obbligatorio'); return; }
    if (!form.societaId)   { setError('Seleziona una società'); return; }
    setSaving(true);
    try {
      if (editId) {
        await apiFetch(`/api/depositi/${editId}`, {
          method: 'PUT',
          body: JSON.stringify({ nome: form.nome.trim(), indirizzo: form.indirizzo || null }),
        });
      } else {
        await apiFetch('/api/depositi', {
          method: 'POST',
          body: JSON.stringify({
            societaId: Number(form.societaId),
            nome: form.nome.trim(),
            tipo: form.tipo,
            indirizzo: form.indirizzo || null,
          }),
        });
      }
      closeModal(); load();
    } catch (e: any) { setError(e.message); }
    finally { setSaving(false); }
  };

  // ── Disattiva ────────────────────────────────────────────────────────────────

  const disattiva = async (d: Deposito) => {
    if (!window.confirm(`Disattivare il deposito "${d.nome}"?`)) return;
    try {
      await apiFetch(`/api/depositi/${d.id}`, { method: 'DELETE' });
      load();
    } catch (e: any) { setError(e.message); }
  };

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <Layout>
      <div className="bop-page-header">
        <div className="bop-page-header__text">
          <h2>{soloFisso ? 'Depositi Fissi' : 'Depositi'}</h2>
          <p>{soloFisso ? 'Magazzini fissi aziendali' : 'Tutti i tipi di deposito: fissi, conto terzi e commessa'}</p>
        </div>
        <button className="bop-btn bop-btn-primary" onClick={openCreate}>+ Nuovo deposito</button>
      </div>

      {/* Filtri */}
      <div className="bop-filter-bar">
        {!isUnica && (
          <select className="bop-select bop-depositi__select-societa"
            value={filtroSocietaId} onChange={e => setFiltroSocietaId(e.target.value)}>
            <option value="">Tutte le società</option>
            {societa.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
          </select>
        )}
        {!soloFisso && (
          <select className="bop-select bop-depositi__select-tipo"
            value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}>
            <option value="">Tutti i tipi</option>
            <option value="FISSO">Fisso</option>
            <option value="CONTO_TERZI">Conto Terzi</option>
            <option value="COMMESSA">Commessa</option>
          </select>
        )}
        <label className="bop-depositi__checkbox-label">
          <input type="checkbox" checked={mostraDisattivi}
            onChange={e => setMostraDisattivi(e.target.checked)} />
          Mostra disattivati
        </label>
      </div>

      {error && !modal && <div className="inline-error">{error}</div>}

      <div className="bop-card">
        <div className="bop-table-wrapper">
          {loading ? (
            <div className="inline-loading">Caricamento…</div>
          ) : items.length === 0 ? (
            <div className="bop-empty-state">Nessun deposito trovato.</div>
          ) : (
            <table className="bop-table bop-table--clickable">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Tipo</th>
                  <th>Indirizzo</th>
                  {!isUnica && <th>Società</th>}
                  <th>Stato</th>
                  <th className="bop-depositi__th-actions"></th>
                </tr>
              </thead>
              <tbody>
                {items.map(d => {
                  // I depositi di commessa non sono modificabili: riga statica, nessuna azione
                  const modificabile = d.tipo !== 'COMMESSA';
                  const rowClass = [
                    d.attivo ? '' : 'bop-depositi__row--disabled',
                    modificabile ? '' : 'bop-table__row--static',
                  ].filter(Boolean).join(' ');
                  return (
                    <tr key={d.id} className={rowClass}
                      onClick={modificabile ? () => openEdit(d) : undefined}>
                      <td><strong>{d.nome}</strong></td>
                      <td><TipoBadge tipo={d.tipo} /></td>
                      <td className="bop-text-muted">{d.indirizzo || '—'}</td>
                      {!isUnica && <td className="bop-text-muted">{d.societaNome ?? d.societaId}</td>}
                      <td>
                        <span className={`bop-badge ${d.attivo ? 'bop-badge--success' : 'bop-badge--danger'}`}>
                          {d.attivo ? 'Attivo' : 'Disattivo'}
                        </span>
                      </td>
                      <td className="bop-text-right">
                        <BopRowActions actions={[
                          { icon: <RowIcon.Disable />, label: 'Disattiva',
                            onClick: () => disattiva(d), variant: 'danger',
                            hidden: !d.attivo || !modificabile },
                        ]} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── Modal ── */}
      {modal && (
        <div className="bop-modal-overlay">
          <div className="bop-modal">
            <div className="bop-modal__header">
              <h3 className="bop-modal__title">
                {editId ? 'Modifica deposito' : 'Nuovo deposito'}
              </h3>
              <button className="bop-modal__close" onClick={closeModal}>×</button>
            </div>

            <div className="bop-modal__body">
              {error && <div className="inline-error bop-depositi__error-mb">{error}</div>}

              {/* Società — solo in creazione se multi-società */}
              {!editId && !isUnica && (
                <div className="bop-form-group">
                  <label className="bop-label">Società <span className="req">*</span></label>
                  <select className="bop-select" value={form.societaId}
                    onChange={e => setForm(f => ({ ...f, societaId: Number(e.target.value) }))}>
                    <option value={0}>Seleziona…</option>
                    {societa.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
                  </select>
                </div>
              )}

              {/* Nome */}
              <div className="bop-form-group">
                <label className="bop-label">Nome <span className="req">*</span></label>
                <input className="bop-input" value={form.nome}
                  placeholder="es. Magazzino Centrale"
                  onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} />
              </div>

              {/* Tipo — solo in creazione e solo nella vista completa, FISSO e CONTO_TERZI */}
              {!editId && !soloFisso && (
                <div className="bop-form-group">
                  <label className="bop-label">Tipo <span className="req">*</span></label>
                  <div className="bop-depositi__tipo-list">
                    {(['FISSO', 'CONTO_TERZI'] as const).map(t => {
                      const selected = form.tipo === t;
                      return (
                        <div key={t}
                          className={`bop-depositi__tipo-card ${selected ? 'bop-depositi__tipo-card--selected' : ''}`}
                          onClick={() => setForm(f => ({ ...f, tipo: t }))}>
                          <div className="bop-depositi__tipo-card-inner">
                            <div className={`bop-depositi__tipo-radio ${selected ? 'bop-depositi__tipo-radio--selected' : ''}`} />
                            <div>
                              <div className="bop-depositi__tipo-label">
                                <TipoBadge tipo={t} />
                              </div>
                              <div className="bop-depositi__tipo-desc">
                                {t === 'FISSO'
                                  ? 'Magazzino fisso aziendale per ricezione e stoccaggio merce'
                                  : 'Merce di fornitori tenuta in deposito presso di voi'}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Indirizzo */}
              <div className="bop-form-group">
                <label className="bop-label">Indirizzo <span className="bop-depositi__optional-hint">(opzionale)</span></label>
                <input className="bop-input" value={form.indirizzo}
                  placeholder="es. Via Roma 1, Milano"
                  onChange={e => setForm(f => ({ ...f, indirizzo: e.target.value }))} />
              </div>
            </div>

            <div className="bop-modal__footer">
              <button className="bop-btn bop-btn-secondary" onClick={closeModal}>Annulla</button>
              <button className="bop-btn bop-btn-primary" onClick={save} disabled={saving}>
                {saving ? 'Salvo…' : 'Salva'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
